import { ShadeDescriptor } from 'base/styleguide/types';
import clamp from 'lodash/clamp';

/**
 * Light mode
 * neutral-0 -> chromatic1-1
 * neutral-1 -> chromatic1-2
 * neutral-6 -> chromatic1-3
 * neutral-7 -> chromatic1-4
 *
 * Dark mode
 * neutral-7 -> chromatic1-7
 * neutral-6 -> chromatic1-6
 * neutral-5 -> chromatic1-5
 * neutral-0 -> chromatic1-4
 */
const NeutralToChromaticShade: Record<
  'light' | 'dark',
  Record<number, number>
> = {
  light: {
    0: 1,
    1: 2,
    6: 3,
    7: 4,
  },
  dark: {
    0: 4,
    5: 5,
    6: 6,
    7: 7,
  },
};

export function neutralToChromaticShade(
  shade: number,
  theme: 'light' | 'dark',
) {
  return NeutralToChromaticShade[theme][shade] ?? shade;
}

/**
 * Light mode
 * neutral-0 -> … -> chromaticN-1 ->
 * neutral-1 -> … -> chromaticN-2 ->
 * neutral-6 -> … -> chromaticN-3 ->
 * neutral-7 -> … -> chromaticN-4 -> neutral-0
 *
 * Dark mode
 * neutral-7 -> … -> chromaticN-7 ->
 * neutral-6 -> … -> chromaticN-6 ->
 * neutral-5 -> … -> chromaticN-5 ->
 * neutral-0 -> … -> chromaticN-4 -> neutral-7
 */
const LoopChromaticToNeutralShade: Record<
  'light' | 'dark',
  Record<number, number>
> = {
  light: {
    1: 1,
    2: 6,
    3: 7,
    4: 0,
  },
  dark: {
    7: 6,
    6: 5,
    5: 0,
    4: 7,
  },
};

function loopChromaticToNeutralShade(shade: number, theme: 'light' | 'dark') {
  // Shade can get off the rails of the standard cycling order, clamp back to
  // some baseline at the end of the palette cycling loop.
  const defaultNeutralShade = theme === 'light' ? 0 : 7;
  return LoopChromaticToNeutralShade[theme][shade] ?? defaultNeutralShade;
}

/**
 * Light mode
 * neutral-0 -> chromatic1-1
 * neutral-1 -> chromatic1-2
 * neutral-6 -> chromatic1-3
 * neutral-7 -> chromatic1-4
 *
 * Dark mode
 * neutral-7 -> chromatic1-7
 * neutral-6 -> chromatic1-6
 * neutral-5 -> chromatic1-5
 * neutral-0 -> chromatic1-4
 */
const ChromaticToNeutralShade: Record<
  'light' | 'dark',
  Record<number, number>
> = {
  light: {
    1: 0,
    2: 1,
    3: 6,
    4: 7,
  },
  dark: {
    4: 0,
    5: 5,
    6: 6,
    7: 7,
  },
};

export function chromaticToNeutralShade(
  shade: number,
  theme: 'light' | 'dark',
) {
  return ChromaticToNeutralShade[theme][shade] ?? clamp(shade, 0, 7);
}

/**
 * This sequence is from here:
 * https://www.figma.com/design/y5isuyQJLH1fzFJ4bafUIj/Styleguide-Builder---V1.0?node-id=2303-88728&t=X1C1iUtKYwNhTHB2-4
 *
 * The wrapping logic is slightly complex, especially due to the difference
 * between light & dark theme behaviour.
 *
 * Light mode
 * neutral-0 -> chromatic1-1 -> chromatic2-1 -> … -> chromaticN-1
 * neutral-1 -> chromatic1-2 -> chromatic2-2 -> … -> chromaticN-2
 * neutral-6 -> chromatic1-3 -> chromatic2-3 -> … -> chromaticN-3
 * neutral-7 -> chromatic1-4 -> chromatic2-4 -> … -> chromaticN-4 -> neutral-0
 *
 * Dark mode
 * neutral-7 -> chromatic1-7 -> chromatic2-7 -> … -> chromaticN-7
 * neutral-6 -> chromatic1-6 -> chromatic2-6 -> … -> chromaticN-6
 * neutral-5 -> chromatic1-5 -> chromatic2-5 -> … -> chromaticN-5
 * neutral-0 -> chromatic1-4 -> chromatic2-4 -> … -> chromaticN-4 -> neutral-7
 */
export function cyclePaletteInSequence(
  scheme: ShadeDescriptor,
  chromaticCount: number,
  theme: 'light' | 'dark',
): ShadeDescriptor {
  if (scheme.palette === 'neutral') {
    // Calculating the shade is a little complex due to the shuffling order
    // e.g. neutral-6 -> chromatic1-3
    return {
      palette: 'chromatic',
      chromatic: 1,
      shade: neutralToChromaticShade(scheme.shade, theme),
    };
  }

  if (scheme.chromatic === chromaticCount) {
    // Final non-neutral, wrap back around to neutral
    return {
      palette: 'neutral',
      shade: loopChromaticToNeutralShade(scheme.shade, theme),
    };
  }

  return {
    ...scheme,
    chromatic: scheme.chromatic + 1,
  };
}

/**
 * Picks the next or previous shade in the sequence:
 * [0] -> 1 -> 2 -> 3 -> 4 -> 5 -> 6 -> 7 -> [0]
 *
 * Note: Primary and chromatic palettes have 7 shades but the neutral palette
 * only has 5 shades.
 *
 * @param scheme The current scheme to pick the next shade from
 * @param direction The direction to pick the next shade in
 */
export function cycleShadeInSequence(
  scheme: ShadeDescriptor,
  direction: 'forward' | 'backward',
): ShadeDescriptor {
  let { palette, shade } = scheme;
  const min = palette === 'neutral' ? 0 : 1;
  const max = 7;

  if (direction === 'backward') {
    if (shade === min) {
      shade = max;
    } else {
      shade--;
    }
  } else {
    if (shade === max) {
      shade = min;
    } else {
      shade++;
    }
  }

  return { ...scheme, shade };
}
